import { ReactNode, useState } from 'react';

type PopoverPosition = {
  vertical: 'top' | 'center' | 'bottom';
  horizontal: 'left' | 'center' | 'right';
};

const DEFAULT_ANCHOR_ORIGIN: PopoverPosition = {
  vertical: 'bottom',
  horizontal: 'center',
};

const DEFAULT_TRANSFORM_ORIGIN: PopoverPosition = {
  vertical: 'top',
  horizontal: 'center',
};

const NESTED_ANCHOR_ORIGIN: PopoverPosition = {
  vertical: 'top',
  horizontal: 'left',
};

const NESTED_TRANSFORM_ORIGIN: PopoverPosition = {
  vertical: 'top',
  horizontal: 'right',
};

type AnchoredMenuContentProps = {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  anchorOrigin: PopoverPosition;
  transformOrigin: PopoverPosition;
};

type AnchoredMenuProps = {
  children: (state: { isOpen: boolean }) => ReactNode;
  renderContent: (props: AnchoredMenuContentProps) => ReactNode;
  nested?: boolean;
  // Disabled triggers (e.g. MUI Button with `disabled`) get `pointer-events: none`
  // and let the click bubble to this wrapper span, opening the menu anyway.
  // Honor a disabled state explicitly so consumers don't have to wrap defensively.
  disabled?: boolean;
};

const AnchoredMenu = ({
  children,
  renderContent,
  nested = false,
  disabled = false,
}: AnchoredMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <span onClick={disabled ? undefined : e => setAnchorEl(e.currentTarget)}>
        {children({ isOpen: open })}
      </span>
      {renderContent({
        anchorEl,
        open,
        onClose: () => setAnchorEl(null),
        anchorOrigin: nested ? NESTED_ANCHOR_ORIGIN : DEFAULT_ANCHOR_ORIGIN,
        transformOrigin: nested
          ? NESTED_TRANSFORM_ORIGIN
          : DEFAULT_TRANSFORM_ORIGIN,
      })}
    </>
  );
};

export { AnchoredMenu };
export type { AnchoredMenuContentProps };
